import { createStore } from "vuex";
import createPersistedState from "vuex-persistedstate";

function uid(prefix = "id") {
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
}

function normPlate(v) {
  return String(v || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
}

function nowIso() {
  return new Date().toISOString();
}

function minutesBetween(startIso, endIso) {
  const a = new Date(startIso).getTime();
  const b = new Date(endIso).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return 0;
  return Math.max(0, Math.round((b - a) / 60000));
}

function guestAmount(minutes, pricing) {
  const firstHourRate = Number(pricing?.firstHourRate ?? 20);
  const extraHourRate = Number(pricing?.extraHourRate ?? 15);
  const hours = Math.max(1, Math.ceil(minutes / 60));
  if (hours <= 1) return firstHourRate;
  return firstHourRate + (hours - 1) * extraHourRate;
}

function monthKey(iso = nowIso()) {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function dayKey(iso = nowIso()) {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default createStore({
  state: {
    _bootstrapped: false,

    auth: {
      token: "",
      userId: "",
    },

    staffUsers: [],
    permanentCustomers: [],

    parkingSessions: [],
    payments: [],

    settings: {
      guestPricing: {
        firstHourRate: 20,
        extraHourRate: 15,
      },
      monthlyFee: 0,
      treatExpiredPermanentAsGuest: true,
    },

    auditLogs: [],
  },

  getters: {
    isBootstrapped: (s) => Boolean(s._bootstrapped),
    authedUser: (s) => s.staffUsers.find((u) => u.id === s.auth.userId) || null,
    isAuthed: (s, g) => Boolean(s.auth.token && g.authedUser && g.authedUser.active),
    role: (s, g) => g.authedUser?.role || "",
    isAdmin: (s, g) => g.role === "admin",
    isOperator: (s, g) => g.role === "operator",

    activeSessions: (s) => s.parkingSessions.filter((x) => x.status === "active"),
    completedSessions: (s) => s.parkingSessions.filter((x) => x.status === "completed"),

    todayRevenue: (s) => {
      const k = dayKey();
      return s.payments
        .filter((p) => p.dayKey === k)
        .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    },
    monthRevenue: (s) => {
      const k = monthKey();
      return s.payments
        .filter((p) => p.monthKey === k)
        .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    },
    expiredPermanentCustomers: (s) => {
      const now = Date.now();
      return s.permanentCustomers.filter((c) => {
        const end = new Date(c.subscriptionEnd).getTime();
        return c.status === "active" && Number.isFinite(end) && end < now;
      });
    },
  },

  mutations: {
    bootstrap(state) {
      if (state._bootstrapped) return;

      if (!Array.isArray(state.staffUsers) || state.staffUsers.length === 0) {
        state.staffUsers = [
          {
            id: uid("staff"),
            name: "System Admin",
            username: "admin",
            password: "admin123",
            role: "admin",
            active: true,
            createdAt: nowIso(),
          },
          {
            id: uid("staff"),
            name: "Gate Operator",
            username: "operator",
            password: "operator123",
            role: "operator",
            active: true,
            createdAt: nowIso(),
          },
        ];
      }

      if (!Array.isArray(state.permanentCustomers)) state.permanentCustomers = [];
      if (!Array.isArray(state.parkingSessions)) state.parkingSessions = [];
      if (!Array.isArray(state.payments)) state.payments = [];
      if (!Array.isArray(state.auditLogs)) state.auditLogs = [];

      state._bootstrapped = true;
    },

    log(state, entry) {
      state.auditLogs.unshift({
        id: uid("log"),
        at: nowIso(),
        ...entry,
      });
      if (state.auditLogs.length > 5000) state.auditLogs.length = 5000;
    },

    login(state, { token, userId }) {
      state.auth.token = token || "";
      state.auth.userId = userId || "";
    },
    logout(state) {
      state.auth.token = "";
      state.auth.userId = "";
    },

    upsertStaff(state, user) {
      const idx = state.staffUsers.findIndex((u) => u.id === user.id);
      if (idx >= 0) state.staffUsers.splice(idx, 1, { ...state.staffUsers[idx], ...user });
      else state.staffUsers.unshift({ ...user, id: user.id || uid("staff"), createdAt: nowIso() });
    },
    setStaffActive(state, { id, active }) {
      const u = state.staffUsers.find((x) => x.id === id);
      if (u) u.active = Boolean(active);
    },

    upsertPermanentCustomer(state, customer) {
      const next = {
        id: customer.id || uid("perm"),
        name: String(customer.name || "").trim(),
        phone: String(customer.phone || "").trim(),
        plateNumber: normPlate(customer.plateNumber),
        subscriptionStart: customer.subscriptionStart,
        subscriptionEnd: customer.subscriptionEnd,
        status: customer.status || "active",
        createdAt: customer.createdAt || nowIso(),
        updatedAt: nowIso(),
      };
      const idx = state.permanentCustomers.findIndex((c) => c.id === next.id);
      if (idx >= 0) state.permanentCustomers.splice(idx, 1, { ...state.permanentCustomers[idx], ...next });
      else state.permanentCustomers.unshift(next);
    },
    setPermanentStatus(state, { id, status }) {
      const c = state.permanentCustomers.find((x) => x.id === id);
      if (c) c.status = status;
    },

    updateSettings(state, patch) {
      state.settings = {
        ...state.settings,
        ...patch,
        guestPricing: {
          ...state.settings.guestPricing,
          ...(patch?.guestPricing || {}),
        },
      };
    },

    createSession(state, session) {
      state.parkingSessions.unshift(session);
    },
    completeSession(state, { sessionId, exitTime, minutes, amount, operatorExitId }) {
      const s = state.parkingSessions.find((x) => x.id === sessionId);
      if (!s || s.status !== "active") return;
      s.exitTime = exitTime;
      s.minutes = minutes;
      s.amount = amount;
      s.operatorExitId = operatorExitId || "";
      s.status = "completed";
    },
    addPayment(state, payment) {
      state.payments.unshift(payment);
    },
  },

  actions: {
    bootstrap({ commit }) {
      commit("bootstrap");
    },

    login({ state, commit }, { username, password }) {
      const u = state.staffUsers.find(
        (x) => x.active && x.username === String(username || "").trim()
      );
      if (!u || u.password !== String(password || "")) {
        return { ok: false, message: "Invalid username or password." };
      }
      const token = `demo-jwt.${uid("t")}`;
      commit("login", { token, userId: u.id });
      commit("log", { action: "login", actorId: u.id, actorRole: u.role, message: "User logged in." });
      return { ok: true, role: u.role };
    },

    logout({ getters, commit }) {
      const actor = getters.authedUser;
      if (actor) commit("log", { action: "logout", actorId: actor.id, actorRole: actor.role, message: "User logged out." });
      commit("logout");
    },

    registerEntry({ state, getters, commit }, { plateNumber }) {
      const actor = getters.authedUser;
      const plate = normPlate(plateNumber);
      if (!actor || actor.role !== "operator") return { ok: false, message: "Operator login required." };
      if (!plate) return { ok: false, message: "Plate number is required." };

      const existingActive = state.parkingSessions.find(
        (x) => x.status === "active" && normPlate(x.plateNumber) === plate
      );
      if (existingActive) return { ok: false, message: "This vehicle is already inside (active session exists)." };

      const perm = state.permanentCustomers.find((c) => normPlate(c.plateNumber) === plate);
      const now = Date.now();
      let type = "guest";
      let permanentCustomerId = "";
      let warning = "";

      if (perm && perm.status === "active") {
        const end = new Date(perm.subscriptionEnd).getTime();
        const valid = Number.isFinite(end) && end >= now;
        if (valid) {
          type = "permanent";
          permanentCustomerId = perm.id;
        } else {
          warning = "Permanent subscription is expired.";
          if (!state.settings.treatExpiredPermanentAsGuest) {
            return { ok: false, message: "Subscription expired. Entry blocked by system setting.", warning };
          }
        }
      }

      const session = {
        id: uid("sess"),
        plateNumber: plate,
        type,
        permanentCustomerId,
        entryTime: nowIso(),
        exitTime: "",
        minutes: 0,
        amount: 0,
        operatorEntryId: actor.id,
        operatorExitId: "",
        status: "active",
      };

      commit("createSession", session);
      commit("log", {
        action: "entry",
        actorId: actor.id,
        actorRole: actor.role,
        message: `Entry registered for ${plate} (${type}).`,
        meta: { plateNumber: plate, type, warning: warning || undefined },
      });

      return { ok: true, session, warning };
    },

    processExit({ state, getters, commit }, { plateNumber, paymentMethod = "cash" }) {
      const actor = getters.authedUser;
      const plate = normPlate(plateNumber);
      if (!actor || actor.role !== "operator") return { ok: false, message: "Operator login required." };
      if (!plate) return { ok: false, message: "Plate number is required." };

      const s = state.parkingSessions.find(
        (x) => x.status === "active" && normPlate(x.plateNumber) === plate
      );
      if (!s) return { ok: false, message: "No active session found for this plate." };

      const exitTime = nowIso();
      const minutes = minutesBetween(s.entryTime, exitTime);
      const amount = s.type === "guest" ? guestAmount(minutes, state.settings.guestPricing) : 0;

      commit("completeSession", {
        sessionId: s.id,
        exitTime,
        minutes,
        amount,
        operatorExitId: actor.id,
      });

      if (amount > 0) {
        const p = {
          id: uid("pay"),
          sessionId: s.id,
          amount,
          paymentMethod,
          createdAt: exitTime,
          dayKey: dayKey(exitTime),
          monthKey: monthKey(exitTime),
          operatorId: actor.id,
        };
        commit("addPayment", p);
      }

      commit("log", {
        action: "exit",
        actorId: actor.id,
        actorRole: actor.role,
        message: `Exit processed for ${plate}. Amount ${amount} ETB.`,
        meta: { plateNumber: plate, minutes, amount, type: s.type },
      });

      return {
        ok: true,
        receipt: {
          plateNumber: plate,
          type: s.type,
          entryTime: s.entryTime,
          exitTime,
          minutes,
          amount,
          paymentMethod: amount > 0 ? paymentMethod : "n/a",
          operator: { id: actor.id, name: actor.name },
          sessionId: s.id,
        },
      };
    },
  },

  plugins: [
    createPersistedState({
      key: "parking_system_store_v1",
    }),
  ],
});
